type MaxDimensionsRecord = {
  width?: number;
  height?: number;
  depth?: number;
};

/** Preferences recovered from a legacy V2 `compartments` blob; absent fields use the defaults. */
export type LegacyOptimizationParameters = {
  desiredAspectRatio?: number;
  maxDimensions?: MaxDimensionsRecord;
};

type LegacyOptimizationSource = {
  desiredAspectRatio?: unknown;
  maxDimensions?: unknown;
};

/** `"CASE"` is `InteriorType.CASE`; `@project/shared` cannot depend on `@project/manufacturer`. */
function isCaseInterior(
  interior: unknown,
): interior is Record<string, unknown> {
  return (
    !!interior &&
    typeof interior === "object" &&
    (interior as { type?: unknown }).type === "CASE"
  );
}

/** A finite number above zero, given as a number or as a string that is only that number. */
function parsePositiveNumber(value: unknown): number | undefined {
  let parsed = Number.NaN;
  if (typeof value === "number") {
    parsed = value;
  } else if (typeof value === "string" && value.trim() !== "") {
    // `Number` rejects trailing garbage such as "1.6abc", unlike `parseFloat`.
    parsed = Number(value);
  }
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** Legacy V2 stores `[width, height, depth]`, newer data a record. */
function normalizeMaxDimensions(
  value: unknown,
): MaxDimensionsRecord | undefined {
  if (!value || typeof value !== "object") return undefined;

  const raw = value as Record<string, unknown>;
  const [width, height, depth] = Array.isArray(value)
    ? value
    : [raw.width, raw.height, raw.depth];
  const record: MaxDimensionsRecord = {};
  const w = parsePositiveNumber(width);
  const h = parsePositiveNumber(height);
  const d = parsePositiveNumber(depth);
  if (w !== undefined) record.width = w;
  if (h !== undefined) record.height = h;
  if (d !== undefined) record.depth = d;
  return Object.keys(record).length > 0 ? record : undefined;
}

function buildOptimizationParameters(
  source: LegacyOptimizationSource | undefined,
): LegacyOptimizationParameters | undefined {
  if (!source || typeof source !== "object") return undefined;

  const desiredAspectRatio = parsePositiveNumber(source.desiredAspectRatio);
  const maxDimensions = normalizeMaxDimensions(source.maxDimensions);
  if (desiredAspectRatio === undefined && maxDimensions === undefined) {
    return undefined;
  }

  const result: LegacyOptimizationParameters = {};
  if (desiredAspectRatio !== undefined) {
    result.desiredAspectRatio = desiredAspectRatio;
  }
  if (maxDimensions !== undefined) {
    result.maxDimensions = maxDimensions;
  }
  return result;
}

function asObject(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : undefined;
}

/** Both places V2 kept the preferences: the direct block, then the selected solution's. */
function readLegacyOptimizationSources(
  legacyCompartments: unknown,
): (LegacyOptimizationSource | undefined)[] {
  const compartments = asObject(legacyCompartments);
  if (!compartments) return [];

  const entity = asObject(asObject(compartments.selectedSolution)?.entity);
  return [
    asObject(compartments.optimizationParameters),
    asObject(entity?.parameters),
  ];
}

/**
 * Optimization preferences stored in a legacy V2 `compartments` blob
 * (`optimizationParameters`, else the selected solution's parameters, also
 * when the direct block holds no valid value). Invalid values are dropped,
 * so the defaults apply to them.
 */
export function readLegacyParams(
  legacyCompartments: unknown,
): LegacyOptimizationParameters | undefined {
  const [direct, selectedSolution] =
    readLegacyOptimizationSources(legacyCompartments);
  // An empty or invalid direct block must not hide the selected solution's values.
  return (
    buildOptimizationParameters(direct) ??
    buildOptimizationParameters(selectedSolution)
  );
}

/**
 * Backfill `interiorOptions.optimizationParameters` from legacy V2
 * `compartments.optimizationParameters` (or selected solution parameters).
 * Does not copy tolerance/trayMaterial — those live on `interiorOptions` already.
 */
export function migrateOptimizationParameters(
  settings: Record<string, unknown>,
  legacyCompartments?: unknown,
): void {
  const interior = settings.interiorOptions;
  if (!isCaseInterior(interior)) return;

  if (
    interior.optimizationParameters &&
    typeof interior.optimizationParameters === "object"
  ) {
    return;
  }

  const migrated = readLegacyParams(legacyCompartments);
  if (migrated) {
    interior.optimizationParameters = migrated;
  }
}
